import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { StatCard }     from '../components/shared/StatCard';
import { Toggle }       from '../components/shared/Toggle';
import { Modal, ConfirmModal } from '../components/shared/Modal';
import { Spinner }      from '../components/shared/Spinner';
import { usersAPI }     from '../services/api';
import { useAuth }      from '../contexts/AuthContext';
import { fmt, ROLE_COLORS, ROLE_LABELS } from '../components/shared/fmt';
import { useTitle }     from '../hooks/useTitle';
import type { UserRole } from '../types';

const ROLES = Object.entries(ROLE_LABELS) as [UserRole, string][];

export const UsersPage: React.FC = () => {
  useTitle('Users & Roles');
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey:['users'], queryFn: () => usersAPI.getAll() });
  const users = data?.data || [];
  const [modal,  setModal]  = useState<null|'form'|'pin'|'delete'>(null);
  const [sel,    setSel]    = useState<any>(null);
  const [search, setSearch] = useState('');
  const [form,   setForm]   = useState({ name:'', email:'', role:'cashier' as UserRole, pin:'' });
  const [pins,   setPins]   = useState({ a:'', b:'' });

  const saveMut   = useMutation({ mutationFn: (d:any) => sel ? usersAPI.update(sel.id,d) : usersAPI.create(d), onSuccess:()=>{ toast.success(sel?'Updated':'Created'); qc.invalidateQueries({queryKey:['users']}); setModal(null); } });
  const pinMut    = useMutation({ mutationFn: ({id,pin}:any) => usersAPI.resetPin(id,pin),   onSuccess:()=>{ toast.success('PIN reset'); setModal(null); } });
  const toggleMut = useMutation({ mutationFn: usersAPI.toggle,  onSuccess:()=> qc.invalidateQueries({queryKey:['users']}) });
  const delMut    = useMutation({ mutationFn: usersAPI.delete,  onSuccess:()=>{ toast.success('Deleted'); qc.invalidateQueries({queryKey:['users']}); setModal(null); } });

  const openAdd  = () => { setSel(null); setForm({name:'',email:'',role:'cashier',pin:''}); setModal('form'); };
  const openEdit = (u:any) => { setSel(u); setForm({name:u.name,email:u.email,role:u.role,pin:''}); setModal('form'); };
  const filtered = users.filter((u:any) => !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()));

  if (isLoading) return <Spinner />;

  return (
    <div>
      <div className="page-title">Users & Roles</div>
      <div className="page-sub">Manage staff accounts, PINs, and access permissions</div>

      <div className="stat-grid">
        {ROLES.map(([k, l]) => (
          <StatCard key={k} label={l} value={users.filter((u:any)=>u.role===k&&u.active).length}
            change={`${users.filter((u:any)=>u.role===k).length} total`} accent={ROLE_COLORS[k]} />
        ))}
      </div>

      <div className="card">
        <div className="card-title">
          Staff Directory
          <div className="card-actions">
            <input className="search-input" placeholder="🔍 Search..." value={search} onChange={e=>setSearch(e.target.value)} />
            <button className="btn-primary" onClick={openAdd}>+ Add User</button>
          </div>
        </div>
        <table className="data-table">
          <thead><tr><th>User</th><th>Role</th><th>Status</th><th>Last Login</th><th>Sessions</th><th>Total Sales</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.map((u:any) => {
              const rc = ROLE_COLORS[u.role] || '#888';
              return (
                <tr key={u.id}>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:34, height:34, borderRadius:'50%', background:rc, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Syne,sans-serif', fontSize:13, fontWeight:800, color:'#fff', flexShrink:0 }}>{u.avatar}</div>
                      <div><div style={{ fontWeight:700 }}>{u.name}</div><div style={{ fontSize:11, color:'var(--muted)' }}>{u.email}</div></div>
                    </div>
                  </td>
                  <td><span className="tag" style={{ background:`${rc}18`, color:rc }}>{ROLE_LABELS[u.role as UserRole]}</span></td>
                  <td><span className={`badge badge-${u.active?'active':'inactive'}`}>{u.active?'Active':'Inactive'}</span></td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{u.lastLogin||'Never'}</td>
                  <td style={{ fontWeight:700 }}>{u.sessions}</td>
                  <td className="text-accent" style={{ fontWeight:700 }}>{u.totalSales>0?fmt(u.totalSales):'—'}</td>
                  <td>
                    <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                      <button className="btn-secondary" onClick={()=>openEdit(u)}>✏️ Edit</button>
                      <button className="btn-secondary" onClick={()=>{setSel(u);setPins({a:'',b:''});setModal('pin');}}>🔑 PIN</button>
                      {u.id !== me?.id && <button className="btn-secondary" onClick={()=>toggleMut.mutate(u.id)}>{u.active?'Deactivate':'Activate'}</button>}
                      {u.id !== me?.id && <button className="btn-secondary btn-danger" onClick={()=>{setSel(u);setModal('delete');}}>🗑</button>}
                      {u.id === me?.id && <span style={{ fontSize:11, color:'var(--accent)', fontWeight:700 }}>● YOU</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal==='form' && (
        <Modal title={sel?'Edit User':'Add New User'} subtitle="Fill in staff details" onClose={()=>setModal(null)}
          onConfirm={()=>saveMut.mutate(sel?{name:form.name,email:form.email,role:form.role}:{...form})}
          confirmText={sel?'Save Changes':'Create User'}>
          <div className="form-row cols-2">
            <div className="form-group"><label className="form-label">Full Name</label><input className="form-input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} /></div>
          </div>
          <div className="form-row cols-2">
            <div className="form-group"><label className="form-label">Role</label>
              <select className="form-select" value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value as UserRole}))}>
                {ROLES.map(([k,l])=><option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            {!sel && <div className="form-group"><label className="form-label">4-Digit PIN</label><input className="form-input" type="password" maxLength={4} value={form.pin} onChange={e=>setForm(f=>({...f,pin:e.target.value.replace(/\D/g,'')}))} placeholder="••••" /></div>}
          </div>
        </Modal>
      )}
      {modal==='pin' && sel && (
        <Modal title={`Reset PIN — ${sel.name}`} onClose={()=>setModal(null)}
          onConfirm={()=>{ if(pins.a.length!==4||pins.a!==pins.b){toast.error('PINs must match, 4 digits');return;} pinMut.mutate({id:sel.id,pin:pins.a}); }}
          confirmText="Reset PIN">
          <div className="form-row cols-2">
            <div className="form-group"><label className="form-label">New PIN</label><input className="form-input" type="password" maxLength={4} value={pins.a} onChange={e=>setPins(p=>({...p,a:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Confirm PIN</label><input className="form-input" type="password" maxLength={4} value={pins.b} onChange={e=>setPins(p=>({...p,b:e.target.value}))} /></div>
          </div>
        </Modal>
      )}
      {modal==='delete' && sel && (
        <ConfirmModal title="Delete User?" message={`This will permanently remove ${sel.name}.`}
          onConfirm={()=>delMut.mutate(sel.id)} onCancel={()=>setModal(null)} danger />
      )}
    </div>
  );
};
